import { IncomingMessage } from 'http';
import { NextRequest } from 'next/server';

import { isDevEnv } from '@/utils/getCurrentEnv';

import { appLogger } from '../app-logger/appLogger';
import { sanitizeReferer } from '../app-logger/redaction/sanitizeUrl';

import { getHeaderValue, type RequestWithHeaders } from './request-extractor';

export type LOB = 'pplus' | 'ptv';

export const LOB_PPLUS: LOB = 'pplus';
export const LOB_PTV: LOB = 'ptv';

/**
 * Whether the Ketch CMP is active. Legacy OneTrust is reachable only for pplus
 * with tn_ketch_enabled off; every other lob (PTV or unknown) uses Ketch.
 */
export const isKetchCmpActive = (lob: LOB, isKetchEnabled: boolean): boolean =>
    lob !== LOB_PPLUS || isKetchEnabled;

// Core LOB resolution logic that can be used by both server and client
const resolveLOBWithFallback = (hostname: string | null, defaultLOB: LOB): LOB => {
    const envLOB = (process.env.LOB || defaultLOB).toLowerCase() as LOB;

    if (!hostname) {
        return envLOB;
    }

    // Check for domains using existing helper
    const detectedLOB = mapDomainToLOB(hostname);
    if (detectedLOB) {
        return detectedLOB;
    }

    // For unknown hosts, log a warning but don't throw - use the default LOB
    appLogger.warn(`Unknown LOB for host: ${hostname}, using default: ${envLOB}`);
    return envLOB;
};

// Server-side LOB detection function
const getLOB = (request?: IncomingMessage | NextRequest, defaultLOB: LOB = LOB_PPLUS): LOB => {
    const envLOB = (process.env.LOB || defaultLOB).toLowerCase() as LOB;

    // If there is no request or the current environment is not local or platypus, return the LOB
    if (!request || !isDevEnv()) {
        return envLOB;
    }

    const hostname = getHostname(request);
    return resolveLOBWithFallback(hostname, defaultLOB);
};

// Client-side LOB detection function
const getClientLOB = (defaultLOB: LOB = LOB_PPLUS): LOB => {
    // Check if we're in a browser environment
    if (typeof window === 'undefined') {
        if (!process.env.LOB) {
            appLogger.warn(
                `[getClientLOB] process.env.LOB is missing during SSR. Falling back to default LOB: ${defaultLOB}`,
            );
        }
        return (process.env.LOB || defaultLOB).toLowerCase() as LOB;
    }

    const hostname = window.location.hostname;
    return resolveLOBWithFallback(hostname, defaultLOB);
};

const mapDomainToLOB = (hostname: string): LOB | null => {
    if (hostname.includes('paramountplus.com')) {
        return LOB_PPLUS;
    } else if (hostname.includes('pluto.tv')) {
        return LOB_PTV;
    }
    return null;
};

/** Parses a raw Host / X-Forwarded-Host header value (comma chain, optional port). */
const extractHostnameFromRawHeaderValue = (raw: string | string[] | undefined): string | null => {
    if (raw == null) return null;
    const value = Array.isArray(raw) ? raw[0] : raw;
    if (!value) return null;
    const firstHost = value.split(',')[0]?.trim();
    if (!firstHost) return null;
    return firstHost.split(':')[0] || null;
};

/**
 * Hostname from `x-forwarded-host` then `host` only (no referer).
 * Use where referer must not influence the resolved host (e.g. cookie Domain).
 */
function getHostnameFromForwardedAndHost(request: RequestWithHeaders): string | null {
    return (
        extractHostnameFromRawHeaderValue(getHeaderValue(request, 'x-forwarded-host')) ??
        extractHostnameFromRawHeaderValue(getHeaderValue(request, 'host')) ??
        null
    );
}

// Helper function to extract hostname from request headers.
// Priority: x-forwarded-host > host > referer.
// Referer is last because it indicates where the user came FROM (e.g. github.com),
// not where the app is hosted.
const getHostname = (request: IncomingMessage | NextRequest): string | null => {
    const fromHeaders = getHostnameFromForwardedAndHost(request);

    if (fromHeaders) {
        return fromHeaders;
    }

    // Read the referer once and reuse it for the guard, parsing, and sanitizing
    // so a mutated request can't yield divergent values across the three reads.
    const refererString = getHeaderValue(request, 'referer');
    if (refererString) {
        try {
            if (refererString.startsWith('http://') || refererString.startsWith('https://')) {
                return new URL(refererString).hostname;
            }
        } catch (error) {
            // Drop the raw referer — it can carry auth codes, emails, or session
            // tokens. Keep only safe signal.
            appLogger.warn('Failed to parse referer as URL', {
                ...sanitizeReferer(refererString),
                errorName: error instanceof Error ? error.name : 'UnknownError',
            });
        }
    }

    return null;
};

export { getClientLOB, getHostname, getHostnameFromForwardedAndHost, getLOB };
